// i18next-extract-mark-ns-start inspirations
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import CTALink from '../../CTALink';
import {
  mobileThresholdPixels, tabletThresholdPixels, frames, colors,
} from '../v4/styledComponents';
import { inspirationsImages, getInspirationsInfos } from './inspirationsData';

import calendarIcon from '../../../assets/ourServices/calendar.svg';

const Container = styled.div`
  margin: 0;
  @media (max-width: ${mobileThresholdPixels}) {
    margin: 0 ${frames.mobile};
  }
`;

const Img = styled.img`
  width: 100%;
`;

const LargeBlock = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 100%;
  gap: 20px;
`;

const SmallBlock = styled.div`
  display: flex;
  max-width: 100%;
  gap: 20px;
`;

const ImageContainer = styled.div`
  margin: 64px 0 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  gap: 20px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin: 24px 0 0;
  }
`;

const BlockContainer = styled.div`
  display: none;
  padding: 18px 30px;
  background-color: ${colors.beige100};
  color: ${colors.black};
  border-radius: 8px;
  flex-wrap: wrap;
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translate(-50%, -0%);
  width: calc(100% - 100px);
`;

const BlockContainerWrapper = styled.div`
  position: relative;
  width: 100%;
  &:hover ${BlockContainer} {
    display: block;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Subtitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  margin: 0;
`;

const Price = styled.span`
  font-size: 12px;
`;

const Text = styled.p`
  font-size: 12px;
  color: ${colors.grey600};
  max-width: ${(props) => (props.smallBlock ? '250px' : '280px')};
`;

const ImageBlockContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Calendar = styled.img`
  height: 20px;
`;

const Inspirations = forwardRef(({ range }, ref) => {
  const { t } = useTranslation();
  const inspirationsInfos = getInspirationsInfos(false, t);
  const { largeImages } = inspirationsImages[range];
  const { smallImages } = inspirationsImages[range];
  const inspirationsData = inspirationsInfos[range];

  return (
    <Container ref={ref}>
      <ImageContainer>
        <LargeBlock>
          {largeImages.map((image, index) => (
            <ImageBlockContainer key={image.toString()}>
              <BlockContainerWrapper>
                <Img src={image} alt={range} />
                {index > 0 && inspirationsData[index - 1]?.title && (
                  <CTALink fromInteraction="Inspirations" linkState={inspirationsData[index - 1].preFilledSteps}>
                    <BlockContainer>
                      <div>
                        <Row>
                          <Subtitle>{inspirationsData[index - 1].title}</Subtitle>
                          <Price>{inspirationsData[index - 1].price}</Price>
                        </Row>
                        <Row>
                          <Text>{inspirationsData[index - 1].text}</Text>
                          <Calendar src={calendarIcon} alt={t('icône calendrier')} />
                        </Row>
                      </div>
                    </BlockContainer>
                  </CTALink>
                )}
              </BlockContainerWrapper>
            </ImageBlockContainer>
          ))}
        </LargeBlock>
        <SmallBlock>
          {smallImages.map((image, index) => (
            <ImageBlockContainer key={image.toString()}>
              <BlockContainerWrapper>
                <Img src={image} alt={range} />
                <CTALink fromInteraction="Inspirations" linkState={inspirationsData[index + 1].preFilledSteps}>
                  <BlockContainer smallBlock>
                    <div>
                      <Subtitle>{inspirationsData[index + 1].title}</Subtitle>
                      <Price>{inspirationsData[index + 1].price}</Price>
                      <Row>
                        <Text smallBlock>{inspirationsData[index + 1].text}</Text>
                        <Calendar src={calendarIcon} alt={t('icône calendrier')} />
                      </Row>
                    </div>
                  </BlockContainer>
                </CTALink>
              </BlockContainerWrapper>
            </ImageBlockContainer>
          ))}
        </SmallBlock>
      </ImageContainer>
    </Container>
  );
});

Inspirations.propTypes = {
  range: PropTypes.string.isRequired,
};

export default Inspirations;
